import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  ClipboardList,
  Calendar,
  AlertTriangle,
  Clock,
  Filter,
  X,
  Cpu,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Tag,
  Flame,
  CheckCircle2,
  Play,
  ArrowRight,
  SlidersHorizontal,
} from "lucide-react";
import { workOrdersAPI } from "../api";
import { formatIstDateTime } from "../utils/date";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";
import clsx from "clsx";
import WorkOrderCreate from "./WorkOrderCreate";

// Status configuration matching SOPs & Maintenance design system
const statusConfig = {
  draft: {
    label: "Draft",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  },
  approved: {
    label: "Approved",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
  },
  in_progress: {
    label: "In Progress",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  completed: {
    label: "Completed",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
  },
  cancelled: {
    label: "Cancelled",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
};

// Priority configuration
const priorityConfig = {
  critical: {
    label: "Critical",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
  },
  high: {
    label: "High",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
  },
  medium: {
    label: "Medium",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
  },
  low: {
    label: "Low",
    badge: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
};

// Type configuration
const typeConfig = {
  preventive: { label: "Preventive", dot: "bg-blue-500" },
  corrective: { label: "Corrective", dot: "bg-amber-500" },
  predictive: { label: "Predictive", dot: "bg-purple-500" },
  inspection: { label: "Inspection", dot: "bg-cyan-500" },
  emergency: { label: "Emergency", dot: "bg-rose-500" },
};

/**
 * Metric KPI Summary Card matching SOPs & Maintenance
 */
const SummaryCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
  const colorThemes = {
    blue: {
      bg: "from-blue-50/90 to-white border-blue-100/80 hover:border-blue-200",
      icon: "bg-blue-100/80 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    amber: {
      bg: "from-amber-50/90 to-white border-amber-100/80 hover:border-amber-200",
      icon: "bg-amber-100/80 text-amber-600",
      badge: "bg-amber-100 text-amber-700",
    },
    rose: {
      bg: "from-rose-50/90 to-white border-rose-100/80 hover:border-rose-200",
      icon: "bg-rose-100/80 text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
    emerald: {
      bg: "from-emerald-50/90 to-white border-emerald-100/80 hover:border-emerald-200",
      icon: "bg-emerald-100/80 text-emerald-600",
      badge: "bg-emerald-100 text-emerald-700",
    },
  };

  const theme = colorThemes[color] || colorThemes.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx(
        "p-4 sm:p-5 bg-gradient-to-br border rounded-2xl shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between relative overflow-hidden group",
        theme.bg
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", theme.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          {subtitle && (
            <span
              className={clsx(
                "inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full truncate",
                theme.badge
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1">
          {title}
        </p>
        <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {value}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Work Order List Item (12-column grid row, NO action column)
 */
const WorkOrderListItem = ({ workOrder, index }) => {
  const navigate = useNavigate();
  const status = statusConfig[workOrder.status] || statusConfig.draft;
  const priority = priorityConfig[workOrder.priority] || priorityConfig.medium;
  const type = typeConfig[workOrder.work_order_type] || {
    label: workOrder.work_order_type || "General",
    dot: "bg-slate-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <div
        onClick={() => navigate(`/work-orders/${workOrder.id}`)}
        className="grid grid-cols-12 items-center px-4 py-3.5 text-sm border-b border-slate-100/80 transition-colors hover:bg-blue-50/30 group cursor-pointer"
      >
        {/* Col 1: WO Number, Title & Machine (span 4) */}
        <div className="col-span-4 flex items-start gap-3 min-w-0 pr-3">
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0 group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200 mt-0.5">
            <ClipboardList className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors text-sm break-all">
                {workOrder.work_order_number}
              </span>
              <span className="text-slate-300">•</span>
              <span className="text-xs text-slate-700 font-medium break-words leading-snug">
                {workOrder.title}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500 mt-1">
              <span className="flex items-center gap-1 font-medium text-slate-600">
                <Cpu className="w-3 h-3 text-slate-400 shrink-0" />
                <span>{workOrder.machine_name || "General Machinery"}</span>
              </span>
              <span className="text-slate-300">•</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium capitalize">
                <span className={clsx("w-1.5 h-1.5 rounded-full", type.dot)} />
                {type.label}
              </span>
            </div>
          </div>
        </div>

        {/* Col 2: Status (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shrink-0",
              status.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", status.dot)} />
            <span>{status.label}</span>
          </span>
        </div>

        {/* Col 3: Priority (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border shrink-0",
              priority.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", priority.dot)} />
            <span>{priority.label}</span>
          </span>
        </div>

        {/* Col 4: Assigned To (span 2) */}
        <div className="col-span-2 flex items-center gap-2 min-w-0 pr-2">
          <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 text-xs font-semibold">
            {workOrder.assigned_to_name ? workOrder.assigned_to_name.charAt(0).toUpperCase() : "?"}
          </div>
          <span className="text-xs font-medium text-slate-700" title={workOrder.assigned_to_name || "Unassigned"}>
            {workOrder.assigned_to_name || "Unassigned"}
          </span>
        </div>

        {/* Col 5: Scheduled Start (span 2) */}
        <div className="col-span-2 flex items-center gap-1.5 text-xs text-slate-600">
          <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span>
            {workOrder.scheduled_start
              ? formatIstDateTime(workOrder.scheduled_start)
              : "Unscheduled"}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const WorkOrders = () => {
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isPriorityOpen, setIsPriorityOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const statusRef = useRef(null);
  const priorityRef = useRef(null);
  const typeRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) setIsStatusOpen(false);
      if (priorityRef.current && !priorityRef.current.contains(event.target)) setIsPriorityOpen(false);
      if (typeRef.current && !typeRef.current.contains(event.target)) setIsTypeOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Primary API Query
  const { data: rawData = [], isLoading, error } = useQuery({
    queryKey: ["work-orders", search, statusFilter],
    queryFn: () =>
      workOrdersAPI
        .getAll({
          search: search || undefined,
          status: statusFilter || undefined,
        })
        .then((response) => response.data.data || []),
  });

  // Client-side filtering for priority and type (plus local search fallback)
  const filteredData = useMemo(() => {
    return rawData.filter((item) => {
      if (priorityFilter && item.priority !== priorityFilter) return false;
      if (typeFilter && item.work_order_type !== typeFilter) return false;
      return true;
    });
  }, [rawData, priorityFilter, typeFilter]);

  // Statistics for KPI Cards
  const totalWorkOrders = rawData.length;
  const inProgressCount = rawData.filter((wo) => wo.status === "in_progress").length;
  const criticalCount = rawData.filter((wo) => wo.priority === "critical" || wo.priority === "high").length;
  const completedCount = rawData.filter((wo) => wo.status === "completed").length;

  // Pagination calculation
  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1;
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredData.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, priorityFilter, typeFilter]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPriorityFilter("");
    setTypeFilter("");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (currentPage >= totalPages - 3)
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header matching SOPs & Maintenance */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Work Orders
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Planned, predictive & emergency maintenance orders awaiting execution
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap self-start sm:self-auto">
          <Link
            to="/maintenance"
            className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 shadow-xs"
          >
            <span>Maintenance Logs</span>
          </Link>

          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>Create Work Order</span>
            </button>
          </HasPermission>
        </div>
      </div>

      {/* 4 Metric Summary Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <SummaryCard
          title="Total Work Orders"
          value={totalWorkOrders}
          subtitle="All Active Orders"
          icon={ClipboardList}
          color="blue"
        />
        <SummaryCard
          title="In Progress"
          value={inProgressCount}
          subtitle={inProgressCount === 0 ? "Fleet Idle" : "Active Service"}
          icon={Play}
          color="amber"
        />
        <SummaryCard
          title="High / Critical"
          value={criticalCount}
          subtitle={criticalCount === 0 ? "Normal" : "High Priority"}
          icon={AlertTriangle}
          color="rose"
        />
        <SummaryCard
          title="Completed"
          value={completedCount}
          subtitle="Verified Done"
          icon={CheckCircle2}
          color="emerald"
        />
      </div>

      {/* Search & Control Filter Bar */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search work order #, title, or machine..."
              className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter Dropdowns */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {/* Status Filter */}
            <div ref={statusRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsStatusOpen((p) => !p)}
                className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-slate-500" />
                  {statusFilter && statusConfig[statusFilter] ? (
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("w-2 h-2 rounded-full", statusConfig[statusFilter].dot)} />
                      <span className="font-semibold text-slate-700">
                        {statusConfig[statusFilter].label}
                      </span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-700">All Status</span>
                  )}
                </div>
                <ChevronDown
                  className={clsx(
                    "w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ml-1.5",
                    isStatusOpen && "rotate-180"
                  )}
                />
              </button>

              <AnimatePresence>
                {isStatusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 sm:left-auto sm:right-0 mt-1.5 w-44 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200 py-1.5 z-30"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setStatusFilter("");
                        setIsStatusOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                        statusFilter === ""
                          ? "text-blue-600 bg-blue-50/60 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>All Status</span>
                      {statusFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setStatusFilter(key);
                          setIsStatusOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                          statusFilter === key
                            ? "text-blue-600 bg-blue-50/60 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                          <span>{config.label}</span>
                        </div>
                        {statusFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Priority Filter */}
            <div ref={priorityRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsPriorityOpen((p) => !p)}
                className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <Flame className="w-3.5 h-3.5 text-slate-500" />
                  {priorityFilter && priorityConfig[priorityFilter] ? (
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("w-2 h-2 rounded-full", priorityConfig[priorityFilter].dot)} />
                      <span className="font-semibold text-slate-700">
                        {priorityConfig[priorityFilter].label}
                      </span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-700">All Priorities</span>
                  )}
                </div>
                <ChevronDown
                  className={clsx(
                    "w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ml-1.5",
                    isPriorityOpen && "rotate-180"
                  )}
                />
              </button>

              <AnimatePresence>
                {isPriorityOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 sm:left-auto sm:right-0 mt-1.5 w-48 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-30"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setPriorityFilter("");
                        setIsPriorityOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                        priorityFilter === ""
                          ? "text-blue-600 bg-blue-50/60 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>All Priorities</span>
                      {priorityFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setPriorityFilter(key);
                          setIsPriorityOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                          priorityFilter === key
                            ? "text-blue-600 bg-blue-50/60 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                          <span>{config.label}</span>
                        </div>
                        {priorityFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Type Filter */}
            <div ref={typeRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsTypeOpen((p) => !p)}
                className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-slate-500" />
                  {typeFilter && typeConfig[typeFilter] ? (
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("w-2 h-2 rounded-full", typeConfig[typeFilter].dot)} />
                      <span className="font-semibold text-slate-700">
                        {typeConfig[typeFilter].label}
                      </span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-700">All Types</span>
                  )}
                </div>
                <ChevronDown
                  className={clsx(
                    "w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ml-1.5",
                    isTypeOpen && "rotate-180"
                  )}
                />
              </button>

              <AnimatePresence>
                {isTypeOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 sm:left-auto sm:right-0 mt-1.5 w-48 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-30"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setTypeFilter("");
                        setIsTypeOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                        typeFilter === ""
                          ? "text-blue-600 bg-blue-50/60 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>All Types</span>
                      {typeFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                    {Object.entries(typeConfig).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setTypeFilter(key);
                          setIsTypeOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-1.5 text-xs text-left transition-colors font-medium",
                          typeFilter === key
                            ? "text-blue-600 bg-blue-50/60 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                          <span>{config.label}</span>
                        </div>
                        {typeFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {(search || statusFilter || priorityFilter || typeFilter) && (
              <button
                type="button"
                onClick={resetFilters}
                className="btn btn-secondary h-9 text-xs px-2.5 text-slate-500 hover:text-slate-800"
                title="Reset all filters"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Work Orders Table Listing View */}
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center p-4 border-b border-slate-100 last:border-b-0 animate-pulse"
              >
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-200 shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-40 bg-slate-200 rounded" />
                    <div className="h-3 w-28 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="h-6 w-24 bg-slate-200 rounded-full" />
                </div>
                <div className="col-span-2">
                  <div className="h-6 w-20 bg-slate-100 rounded-lg" />
                </div>
                <div className="col-span-2">
                  <div className="h-5 w-24 bg-slate-100 rounded-lg" />
                </div>
                <div className="col-span-2">
                  <div className="h-4 w-28 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : paginatedData.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Table Header (4-2-2-2-2 = 12 cols, matching list item layout) */}
              <div className="grid grid-cols-12 items-center text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/80 border-b border-slate-200/80 px-4 py-3">
                <div className="col-span-4">Work Order / Machine</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Priority</div>
                <div className="col-span-2">Assigned To</div>
                <div className="col-span-2">Scheduled Start</div>
              </div>

              {/* List Body */}
              <div className="divide-y divide-slate-100/90">
                <AnimatePresence>
                  {paginatedData.map((item, index) => (
                    <WorkOrderListItem
                      key={item.id}
                      workOrder={item}
                      index={index}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Pagination Controls */}
          {filteredData.length > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3.5 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, filteredData.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">
                  {filteredData.length}
                </span>{" "}
                work orders
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <ClipboardList className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
            No Work Orders Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
            {search || statusFilter || priorityFilter || typeFilter
              ? "No work orders matched your filter criteria. Try clearing or resetting the filters."
              : "No planned or predictive maintenance work orders have been created yet."}
          </p>
          {search || statusFilter || priorityFilter || typeFilter ? (
            <button
              onClick={resetFilters}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-4"
            >
              Reset Filters
            </button>
          ) : (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="btn btn-primary inline-flex text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
              >
                <Plus className="w-4 h-4" />
                Create Work Order
              </button>
            </HasPermission>
          )}
        </div>
      )}

      {/* Create Work Order Popup Modal */}
      <WorkOrderCreate
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};

export default WorkOrders;
